import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnBarra, PlnBloco, stickyTh } from './PanelShell'
import { SideModal } from './SideModal'
import {
  FiLoader, FiSearch, FiX, FiRefreshCw, FiDownload, FiAlertTriangle, FiSend,
  FiMessageCircle, FiCheckCircle, FiClock, FiUser, FiDollarSign, FiChevronUp,
  FiChevronDown, FiEye, FiMousePointer, FiZap, FiExternalLink, FiFileText,
  FiSlash, FiFilter,
} from 'react-icons/fi'

// Painel Campanhas (2026-08-24) — o rastreio vivo da campanha de cobrança e o
// apoio ao comissionamento da última líder conhecida.
//
// A tela junta três origens que envelhecem em ritmos diferentes:
//  · o SNAPSHOT congelado (grupo, líder, dívida de referência) — muda só quando
//    alguém manda reprocessar;
//  · o ENGAJAMENTO ao vivo (entrega, leitura, clique, bot) — muda sozinho, vem
//    do crm a cada carregamento;
//  · as ANOTAÇÕES do atendimento — mudam aqui na tela.
//
// O relatório em PDF/planilha que isto substitui mostrava 1 acordo fechado
// quando já havia 2. Por isso o cabeçalho sempre mostra QUANDO cada parte foi
// atualizada: um número sem data de validade é como o erro começa.
//
// Revisão de usabilidade (2026-08-24, tarde). O que mudou e por quê:
//  · A tela fazia DUAS chamadas (/resumo e /rastreio) e cada uma disparava a
//    consulta de engajamento no crm. Todo número de /resumo é derivável das
//    linhas — então /resumo saiu e o crm passou a ser consultado uma vez só.
//  · Os KPIs viraram FUNIL: oito caixas iguais escondiam justamente a queda
//    entre etapas, que é o que se olha num disparo. Agora cada etapa mostra a
//    barra, a perda para a etapa anterior, e FILTRA a tabela ao ser clicada.
//  · "226 líderes · 745 ativas" contava coisas diferentes com o mesmo
//    substantivo: 226 era líder distinta e 745 era DEVEDORA cuja líder está
//    ativa. Número maior que o total, lado a lado, é erro esperando acontecer.
//  · O aviso de régua desligada era um chip no fim de uma linha de texto miúdo.
//    É o fato mais consequente da tela (com a régua fora de 'campanha' as
//    etapas do bot param de crescer) — virou faixa.
//  · A aba ativa era ilegível nos dois temas: `.neu-lab .neu-tab` pinta o fundo
//    com !important e derrubava o `bg-primary`, deixando texto claro sobre
//    fundo claro. Passou a usar `neu-seg`, o segmentado padrão da casa.
//  · O drawer mostrava oito campos frios e nenhum sinal de engajamento, embora
//    a linha já trouxesse hora de entrega, leitura, clique, resposta e etapa do
//    bot. Agora tem a jornada da mensagem e o atalho para a conversa.

interface Linha {
  codigo: string
  nome: string | null
  telefone: string
  saldo_snapshot: number
  saldo_disparo: number | string | null
  saldo_atual: number | string | null
  valor_recuperado: number | string | null
  ult_pagamento_erp: string | null
  titulos: number
  dias_atraso: number | null
  ult_semana: number | null
  grupo_cod: string | null
  grupo_nome: string | null
  grupo_extinto: boolean
  distrito_nome: string | null
  lider_cod: string | null
  lider_nome: string | null
  lider_fone: string | null
  lider_ativa: boolean
  lider_match: string | null
  lider_ult_semana: number | null
  eh_propria_lider: boolean
  codigo_origem: string
  envio_status: string | null
  erro: string | null
  enviado_em: string | null
  entregue_em: string | null
  lida_em: string | null
  clicou_em: string | null
  cliques: number
  respondeu: boolean
  msgs_recebidas: number
  bot_estado: string | null
  bot_conversation_id: string | null
  acordo_origem: 'bot' | 'manual' | null
  acordo_qtd: number | null
  valor_ofertado: number | null
  parcelas: number | null
  acordo_parcelas_pagas: number | null
  acordo_parcelas_total: number | null
  acordo_valor_pago: number | string | null
  acordo_ult_pagamento: string | null
  pagamento_atualizado_em: string | null
  promessa_data: string | null
  promessa_valor: number | string | null
  lider_acionada_em: string | null
  lider_acionada_nome: string | null
  eventos: number
  desfecho: string
}

interface Evento {
  id: number
  tipo: string
  texto: string | null
  promessa_data: string | null
  promessa_valor: string | null
  autor_nome: string | null
  criado_em: string
}

const brl = (n: number | string | null) =>
  Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const num = (n: number | null | undefined) => Number(n || 0).toLocaleString('pt-BR')
// DATE do mysql2 chega como ISO; cortar a string mantém o dia que foi gravado.
const dia = (s: string | null) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '')
const dataHora = (s: string | null) =>
  s ? new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''
const semana = (n: number | null) => (n ? `${String(n).slice(0, 4)}/S${String(n).slice(4)}` : '')
const fone = (s: string | null) => {
  const d = String(s || '').replace(/\D/g, '').replace(/^55/, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return s || ''
}
// wa.me exige o número com país. O snapshot guarda a devedora com 55 e a líder
// sem — normalizar aqui evita link que abre conversa vazia.
const waHref = (s: string | null) => {
  const d = String(s || '').replace(/\D/g, '')
  if (!d) return null
  return `https://wa.me/${d.length <= 11 ? '55' + d : d}`
}
// 1839 dias não se lê. "5a 0m" se lê — o dia exato fica no title e no CSV.
const atrasoTxt = (d: number | null) => {
  if (d == null) return '—'
  if (d < 0) return 'a vencer'
  if (d < 60) return `${d} d`
  if (d < 365) return `${Math.round(d / 30)} m`
  const a = Math.floor(d / 365), m = Math.round((d % 365) / 30)
  return m ? `${a}a ${m}m` : `${a}a`
}
// Data local em AAAA-MM-DD. toISOString() daria o dia seguinte à noite (UTC-4).
const emDias = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const hoje = () => emDias(0)

// A cor segue a escada do funil: quanto mais perto do dinheiro, mais verde.
const DESFECHO_CLS: Record<string, string> = {
  'PAGOU (baixou no ERP)': 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-semibold',
  'PAGOU (acordo)': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold',
  'Acordo fechado': 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
  'Negociando': 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  'Passou p/ humano': 'bg-amber-500/15 text-amber-700 dark:text-amber-500',
  'Oferta enviada': 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400',
  'Respondeu': 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  'Clicou no link': 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  'Leu': 'bg-sky-500/10 text-sky-700 dark:text-sky-500',
  'Entregue': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  'Falhou': 'bg-red-500/15 text-red-700 dark:text-red-400',
  'Só enviada': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
}
// Ordem do funil — usada para ordenar a coluna Desfecho por avanço, não por
// alfabeto (que colocaria "Acordo fechado" antes de "Respondeu").
const DESFECHO_ORDEM = [
  'Falhou', 'Só enviada', 'Entregue', 'Leu', 'Clicou no link', 'Respondeu',
  'Oferta enviada', 'Passou p/ humano', 'Negociando', 'Acordo fechado',
  'PAGOU (acordo)', 'PAGOU (baixou no ERP)',
]

const ETAPA_BOT: Record<string, string> = {
  oferta_enviada: 'Oferta enviada',
  handoff: 'Passou p/ humano',
  aguardando_data: 'Negociando',
  gerando_boleto: 'Gerando boleto',
  boleto_gerado: 'Boleto gerado',
  claimed: 'Iniciado',
}

// O funil. Cada etapa sabe (a) como se conta e (b) como se filtra — são a mesma
// função, então o número do funil e o tamanho da tabela nunca divergem.
//
// ⚠️ "alcançou a etapa" ≠ "parou na etapa". O select de Desfecho filtra onde a
// pessoa PAROU; clicar na etapa do funil filtra quem CHEGOU até ali. Quem
// respondeu também consta em Entregues — é o mesmo critério do backend.
const ETAPAS: { k: string; label: string; tem: (l: Linha) => boolean; barra: string }[] = [
  { k: 'enviadas', label: 'Enviadas', tem: l => l.envio_status === 'sent', barra: 'bg-slate-400/70' },
  { k: 'entregues', label: 'Entregues', tem: l => !!l.entregue_em, barra: 'bg-slate-400/80' },
  { k: 'lidas', label: 'Leram', tem: l => !!l.lida_em, barra: 'bg-sky-500/60' },
  { k: 'clicaram', label: 'Clicaram no link', tem: l => !!l.clicou_em, barra: 'bg-blue-500/60' },
  { k: 'responderam', label: 'Responderam', tem: l => !!l.respondeu, barra: 'bg-violet-500/60' },
  { k: 'oferta', label: 'Oferta do bot', tem: l => l.bot_estado === 'oferta_enviada', barra: 'bg-indigo-500/60' },
  // Acordo é acordo: o do bot traz `bot_estado`, o do atendimento só existe
  // porque o job de pagamentos casou o CPF no Asaas. Contar só o primeiro fazia
  // a tela exibir 2 de 26.
  { k: 'acordo', label: 'Acordo fechado', tem: l => Number(l.acordo_parcelas_total) > 0 || l.bot_estado === 'boleto_gerado', barra: 'bg-teal-500/70' },
  { k: 'pagaram', label: 'Pagaram', tem: l => Number(l.valor_recuperado) > 0 || Number(l.acordo_parcelas_pagas) > 0, barra: 'bg-emerald-500/80' },
]

// Os marcos que viram bolinha na tabela e linha do tempo no drawer.
const MARCOS: { k: string; label: string; icon: any; cor: string; tem: (l: any) => boolean; quando?: (l: any) => string | null; n?: (l: any) => number }[] = [
  { k: 'entregue', label: 'Entregue', icon: FiCheckCircle, cor: 'text-slate-600 dark:text-slate-300', tem: l => !!l.entregue_em, quando: l => l.entregue_em },
  { k: 'lida', label: 'Leu', icon: FiEye, cor: 'text-sky-600 dark:text-sky-400', tem: l => !!l.lida_em, quando: l => l.lida_em },
  { k: 'clique', label: 'Clicou no link', icon: FiMousePointer, cor: 'text-blue-600 dark:text-blue-400', tem: l => !!l.clicou_em, quando: l => l.clicou_em, n: l => l.cliques },
  { k: 'resposta', label: 'Respondeu', icon: FiMessageCircle, cor: 'text-violet-600 dark:text-violet-400', tem: l => !!l.respondeu, n: l => l.msgs_recebidas },
  { k: 'bot', label: 'Bot', icon: FiZap, cor: 'text-indigo-600 dark:text-indigo-400', tem: l => !!l.bot_estado },
  { k: 'acordo', label: 'Acordo', icon: FiFileText, cor: 'text-teal-600 dark:text-teal-400', tem: l => Number(l.acordo_parcelas_total) > 0, n: l => l.acordo_qtd || 0 },
]

// Ordenação genérica. Vazio vai sempre para o fim, nos dois sentidos: uma
// coluna com metade em branco não deve "vencer" a ordenação decrescente.
function ordenar<T>(arr: T[], val: (x: T) => any, dir: number, desempate?: (x: T) => any) {
  const cmp = (x: any, y: any, d: number) => {
    const vx = x == null || x === '', vy = y == null || y === ''
    if (vx && vy) return 0
    if (vx) return 1
    if (vy) return -1
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * d
    return String(x).localeCompare(String(y), 'pt-BR') * d
  }
  return [...arr].sort((a, b) =>
    cmp(val(a), val(b), dir) || (desempate ? cmp(desempate(a), desempate(b), -1) : 0))
}

type Ord = { col: string; dir: number }

export function CampanhasPanel({ onOpenConversation }: { onOpenConversation?: (conversationId: string) => void }) {
  const { authenticatedFetch, user } = useAuth()
  const [linhas, setLinhas] = useState<Linha[]>([])
  const [campanha, setCampanha] = useState<any>(null)
  const [bot, setBot] = useState<any>(null)
  const [snapshotEm, setSnapshotEm] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [precisaSnapshot, setPrecisaSnapshot] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [aba, setAba] = useState<'devedoras' | 'lideres'>('devedoras')
  const [q, setQ] = useState('')
  const [fDesfecho, setFDesfecho] = useState('')
  const [fDistrito, setFDistrito] = useState('')
  const [fLider, setFLider] = useState('')
  const [fEtapa, setFEtapa] = useState('')
  const [soPendentes, setSoPendentes] = useState(false)
  const [limite, setLimite] = useState(200)
  const [aberta, setAberta] = useState<Linha | null>(null)
  const [campanhaId, setCampanhaId] = useState<string | null>(null)
  // Em tela de notebook o cabeçalho inteiro come metade da tabela. Fica a
  // escolha de recolher o funil, lembrada por navegador.
  const [funilAberto, setFunilAberto] = useState(() => {
    try { return localStorage.getItem('fin_campanhas_funil') !== '0' } catch { return true }
  })
  useEffect(() => {
    try { localStorage.setItem('fin_campanhas_funil', funilAberto ? '1' : '0') } catch { /* modo privado */ }
  }, [funilAberto])
  const [ordD, setOrdD] = useState<Ord>({ col: 'divida', dir: -1 })
  const [ordL, setOrdL] = useState<Ord>({ col: 'recuperado', dir: -1 })

  const seqRef = useRef(0)
  const carregar = useCallback(async (cid?: string) => {
    const seq = ++seqRef.current
    setLoading(true); setError(null)
    try {
      let id = cid || campanhaId
      if (!id) {
        const r = await authenticatedFetch('/api/campanhas')
        const d = await r.json()
        id = d.campanhas?.[0]?.campaign_id || d.padrao
        setCampanhaId(id)
      }
      // Só /rastreio: todo número do antigo /resumo sai das próprias linhas, e
      // cada chamada custa uma consulta de engajamento no crm.
      const rt = await authenticatedFetch(`/api/campanhas/${id}/rastreio`)
      const dt = await rt.json()
      if (seq !== seqRef.current) return
      if (dt.precisaSnapshot) { setPrecisaSnapshot(true); setLinhas([]); return }
      if (!rt.ok) throw new Error(dt.error || 'falha ao carregar o rastreio')
      setPrecisaSnapshot(false)
      setCampanha(dt.campanha || null); setBot(dt.bot || null)
      setSnapshotEm(dt.snapshot_em || null); setLinhas(dt.linhas || [])
    } catch (e: any) {
      if (seq === seqRef.current) setError(e.message || 'erro ao carregar')
    } finally {
      if (seq === seqRef.current) setLoading(false)
    }
  }, [authenticatedFetch, campanhaId])

  useEffect(() => { carregar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const acao = async (path: string, label: string) => {
    setBusy(label); setError(null)
    try {
      const r = await authenticatedFetch(`/api/campanhas/${campanhaId}/${path}`, { method: 'POST', body: '{}' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'falhou')
      await carregar()
    } catch (e: any) { setError(e.message) } finally { setBusy(null) }
  }

  const distritos = useMemo(
    () => [...new Set(linhas.map(l => l.distrito_nome).filter(Boolean))].sort() as string[], [linhas])
  const desfechos = useMemo(
    () => [...new Set(linhas.map(l => l.desfecho))]
      .sort((a, b) => DESFECHO_ORDEM.indexOf(b) - DESFECHO_ORDEM.indexOf(a)), [linhas])
  const liderFiltrada = useMemo(
    () => (fLider ? linhas.find(l => String(l.lider_cod) === fLider)?.lider_nome || fLider : ''), [linhas, fLider])

  // Recorte SEM a etapa: é sobre ele que o funil é desenhado, para que clicar
  // numa etapa seja um aprofundamento do recorte e não uma troca de assunto.
  const recorte = useMemo(() => {
    const t = q.trim().toLowerCase()
    return linhas.filter(l => {
      if (fDesfecho && l.desfecho !== fDesfecho) return false
      if (fDistrito && l.distrito_nome !== fDistrito) return false
      if (fLider && String(l.lider_cod) !== fLider) return false
      if (soPendentes && (Number(l.valor_recuperado) > 0 || Number(l.acordo_parcelas_pagas) > 0)) return false
      if (!t) return true
      return (l.nome || '').toLowerCase().includes(t) || l.codigo.includes(t) ||
        (l.lider_nome || '').toLowerCase().includes(t) || l.telefone.includes(t) ||
        (l.grupo_nome || '').toLowerCase().includes(t)
    })
  }, [linhas, q, fDesfecho, fDistrito, fLider, soPendentes])

  const filtradas = useMemo(() => {
    const et = ETAPAS.find(e => e.k === fEtapa)
    return et ? recorte.filter(et.tem) : recorte
  }, [recorte, fEtapa])

  useEffect(() => { setLimite(200) }, [q, fDesfecho, fDistrito, fLider, fEtapa, soPendentes])

  // Tudo o que o cabeçalho mostra sai daqui — inclusive os números que antes
  // vinham do /resumo. Segue o recorte: filtrou por distrito, o funil é do
  // distrito.
  const totais = useMemo(() => {
    const L = recorte
    const funil = ETAPAS.map(e => ({ ...e, v: L.filter(e.tem).length }))
    const lideresAtivas = new Set<string>(), lideresTodas = new Set<string>(), lideresAvisadas = new Set<string>()
    let divida = 0, recuperado = 0, acordoPago = 0, falhas = 0, propriaLider = 0, semLider = 0
    let promessasAbertas = 0, promessasVencidas = 0, pagamentoEm: string | null = null
    const h = hoje()
    for (const l of L) {
      divida += Number(l.saldo_snapshot); recuperado += Number(l.valor_recuperado || 0)
      acordoPago += Number(l.acordo_valor_pago || 0)
      if (l.envio_status === 'failed') falhas++
      if (l.eh_propria_lider) propriaLider++
      else if (!l.lider_cod) semLider++
      else {
        lideresTodas.add(l.lider_cod)
        if (l.lider_ativa) lideresAtivas.add(l.lider_cod)
        if (l.lider_acionada_em) lideresAvisadas.add(l.lider_cod)
      }
      const pago = Number(l.valor_recuperado) > 0 || Number(l.acordo_parcelas_pagas) > 0
      if (l.promessa_data && !pago) {
        promessasAbertas++
        if (String(l.promessa_data).slice(0, 10) < h) promessasVencidas++
      }
      if (l.pagamento_atualizado_em && (!pagamentoEm || l.pagamento_atualizado_em > pagamentoEm)) {
        pagamentoEm = l.pagamento_atualizado_em
      }
    }
    return {
      total: L.length, funil, divida, recuperado, acordoPago, falhas, propriaLider, semLider,
      lideres: lideresTodas.size, lideresAtivas: lideresAtivas.size, lideresAvisadas: lideresAvisadas.size,
      promessasAbertas, promessasVencidas, pagamentoEm,
    }
  }, [recorte])

  // Agregado por líder — a tela de comissionamento. Quem era a própria líder
  // fica num balde à parte: não se comissiona alguém para cobrar de si.
  const porLider = useMemo(() => {
    const m = new Map<string, any>()
    for (const l of filtradas) {
      const k = l.eh_propria_lider ? '__propria__' : (l.lider_cod || '__sem__')
      if (!m.has(k)) m.set(k, {
        cod: l.eh_propria_lider ? null : l.lider_cod,
        nome: l.eh_propria_lider ? 'DEVEDORA ERA A PRÓPRIA LÍDER' : (l.lider_nome || 'SEM LÍDER IDENTIFICADA'),
        fone: l.eh_propria_lider ? null : l.lider_fone,
        ativa: l.lider_ativa && !l.eh_propria_lider,
        propria: l.eh_propria_lider,
        devedoras: 0, divida: 0, recuperado: 0, pagaram: 0, responderam: 0, acionadas: 0,
        distritos: new Set<string>(),
      })
      const a = m.get(k)
      a.devedoras++; a.divida += Number(l.saldo_snapshot)
      a.recuperado += Number(l.valor_recuperado || 0)
      if (Number(l.valor_recuperado) > 0 || Number(l.acordo_parcelas_pagas) > 0) a.pagaram++
      if (l.respondeu) a.responderam++
      if (l.lider_acionada_em) a.acionadas++
      if (l.distrito_nome) a.distritos.add(l.distrito_nome)
    }
    return [...m.values()]
  }, [filtradas])

  const baixarCsv = async () => {
    setBusy('csv')
    try {
      const r = await authenticatedFetch(`/api/campanhas/${campanhaId}/export.csv`)
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `rastreio-campanha-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) { setError(e.message) } finally { setBusy(null) }
  }

  // XLSX por import dinâmico (o mesmo padrão do RelatoriosHub): não pesa o
  // bundle de quem nunca clica em exportar.
  const baixarXlsx = async () => {
    setBusy('xlsx')
    try {
      const XLSX = await import('xlsx')
      const matriz = filtradas.map(l => ({
        'Código': l.codigo, 'Consultora': l.nome, 'Telefone': fone(l.telefone),
        'Cobrado na mensagem': Number(l.saldo_disparo || 0),
        'Dívida de referência': Number(l.saldo_snapshot),
        'Dívida hoje': Number(l.saldo_atual || 0),
        'Recuperado no ERP': Number(l.valor_recuperado || 0),
        'Títulos': l.titulos, 'Atraso (dias)': l.dias_atraso,
        'Últ. atividade': semana(l.ult_semana),
        'Grupo': l.grupo_nome, 'Grupo extinto': l.grupo_extinto ? 'Sim' : 'Não',
        'Distrito': l.distrito_nome,
        'Última líder': l.eh_propria_lider ? 'ELA MESMA (era líder)' : l.lider_nome,
        'Telefone da líder': l.eh_propria_lider ? '' : fone(l.lider_fone),
        'Líder ativa': l.eh_propria_lider ? '' : (l.lider_ativa ? 'Sim' : 'Não'),
        'Confiança do vínculo': l.lider_match === 'exato' ? 'Exato' : l.lider_match === 'aprox' ? 'Aproximado' : '',
        'Entregue': dataHora(l.entregue_em), 'Lida': dataHora(l.lida_em), 'Clicou': dataHora(l.clicou_em),
        'Cliques': l.cliques, 'Respondeu': l.respondeu ? 'Sim' : 'Não',
        'Etapa no bot': l.bot_estado ? (ETAPA_BOT[l.bot_estado] || l.bot_estado) : '',
        'Acordos': l.acordo_qtd ?? '', 'Acordo fechado por': l.acordo_origem === 'manual' ? 'Atendimento' : l.acordo_origem === 'bot' ? 'Bot' : '',
        'Acordo pagas': l.acordo_parcelas_pagas ?? '', 'Acordo parcelas': l.acordo_parcelas_total ?? '',
        'Acordo valor pago': Number(l.acordo_valor_pago || 0),
        'Promessa': dia(l.promessa_data), 'Promessa valor': Number(l.promessa_valor || 0),
        'Líder acionada': dataHora(l.lider_acionada_em),
        'Desfecho': l.desfecho,
      }))
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(matriz)
      ws['!autofilter'] = { ref: ws['!ref'] as string }
      XLSX.utils.book_append_sheet(wb, ws, 'Devedoras')
      const wl = XLSX.utils.json_to_sheet(porLider.map(a => ({
        'Líder': a.nome, 'Telefone': fone(a.fone), 'Ativa': a.propria ? '' : (a.ativa ? 'Sim' : 'Não'),
        'Devedoras': a.devedoras, 'Dívida': a.divida, 'Recuperado': a.recuperado,
        'Pagaram': a.pagaram, 'Responderam': a.responderam, 'Avisadas': a.acionadas,
        'Distritos': [...a.distritos].join(', '),
      })))
      wl['!autofilter'] = { ref: wl['!ref'] as string }
      XLSX.utils.book_append_sheet(wb, wl, 'Por líder')
      XLSX.writeFile(wb, `rastreio-campanha-${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (e: any) { setError(e.message) } finally { setBusy(null) }
  }

  const limparFiltros = () => {
    setQ(''); setFDesfecho(''); setFDistrito(''); setFLider(''); setFEtapa(''); setSoPendentes(false)
  }
  const temFiltro = !!(q || fDesfecho || fDistrito || fLider || fEtapa || soPendentes)
  // O funil ignora fEtapa de propósito (é o que a seleção aprofunda), então o
  // rótulo "do recorte" também tem que ignorar.
  const recorteAtivo = !!(q || fDesfecho || fDistrito || fLider || soPendentes)

  if (loading && !linhas.length && !precisaSnapshot) {
    return <PanelShell titulo={TITULO_CAMPANHAS} sub={SUB_CAMPANHAS} icone={<FiSend />}><div className="flex-1 flex items-center justify-center text-muted-foreground">
      <FiLoader className="w-5 h-5 animate-spin mr-2" /> carregando o rastreio…
    </div></PanelShell>
  }

  if (precisaSnapshot) {
    return <PanelShell titulo={TITULO_CAMPANHAS} sub={SUB_CAMPANHAS} icone={<FiSend />}><div className="flex-1 flex items-center justify-center">
      <div className="max-w-md text-center space-y-4">
        <FiSend className="w-10 h-10 mx-auto text-muted-foreground" />
        <h2 className="text-lg font-semibold">O rastreio ainda não foi carregado</h2>
        <p className="text-sm text-muted-foreground">
          A apuração busca no ERP o último grupo e a última líder de cada devedora
          da campanha. Leva alguns segundos e só precisa ser feita uma vez —
          depois disso o painel abre na hora.
        </p>
        {user?.isAdmin ? (
          <button onClick={() => acao('snapshot', 'snapshot')} disabled={!!busy}
            className="neu-btn px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-60">
            {busy === 'snapshot' ? <><FiLoader className="inline w-4 h-4 animate-spin mr-2" />apurando…</> : 'Carregar o rastreio'}
          </button>
        ) : <p className="text-sm text-amber-600">Peça a um administrador para carregar.</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div></PanelShell>
  }

  return (
    // PLANOS (21/10/2026): o título que o Cabecalho desenhava (h1 + linha do
    // template/disparo/total/dívida) virou `titulo`/`sub` do shell; os botões
    // que ficavam ao lado dele foram para `acoes` (AcoesCampanha). O resto do
    // Cabecalho (faixas de alerta, funil, dinheiro e datas de apuração/
    // conferência) continua no `toolbar`, sem perder nada.
    <PanelShell
      titulo={TITULO_CAMPANHAS}
      sub={<>
        {campanha?.template_name} · disparo em {dataHora(campanha?.started_at)} ·{' '}
        {num(linhas.length)} devedoras · {brl(totais.divida)} de dívida de referência
      </>}
      icone={<FiSend />}
      acoes={<AcoesCampanha
        busy={busy} loading={loading} isAdmin={!!user?.isAdmin}
        onAtualizarPagamentos={() => acao('pagamentos', 'pagamentos')}
        onReprocessar={() => acao('snapshot', 'snapshot')}
        onRecarregar={() => carregar()}
        onCsv={baixarCsv} onXlsx={baixarXlsx}
      />}
      toolbar={<Cabecalho
      bot={bot} snapshotEm={snapshotEm} totais={totais} error={error}
      temFiltro={recorteAtivo}
      funilAberto={funilAberto} onFunil={() => setFunilAberto(v => !v)}
      fEtapa={fEtapa} onEtapa={(k: string) => setFEtapa(e => (e === k ? '' : k))}
    />}>
      {/* PLANOS (21/10/2026): filtros numa PlnBarra de duas linhas — a dos
          chips de filtro ativo só aparece com recorte. */}
      <PlnBarra className="pln-barra-dupla">
      <div className="pln-barra-linha text-sm">
        <div className="neu-seg inline-flex items-center gap-1">
          {([['devedoras', `Devedoras (${num(filtradas.length)})`], ['lideres', `Por líder (${num(porLider.length)})`]] as const).map(([k, rot]) => (
            <button key={k} onClick={() => setAba(k as any)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${aba === k
                ? 'neu-relief bg-primary/10 text-primary'
                : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
              {rot}
            </button>
          ))}
        </div>

        <div className="relative">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="nome, código, telefone, líder, grupo…"
            className="neu-input pl-8 pr-7 py-1.5 rounded-lg bg-card border border-border text-sm w-72" />
          {q && <button onClick={() => setQ('')} title="limpar busca"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><FiX className="w-3.5 h-3.5" /></button>}
        </div>

        <select value={fDesfecho} onChange={e => setFDesfecho(e.target.value)}
          title="Onde a pessoa PAROU no funil"
          className="neu-input px-2 py-1.5 rounded-lg bg-card border border-border">
          <option value="">Parou em: qualquer etapa</option>
          {desfechos.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={fDistrito} onChange={e => setFDistrito(e.target.value)}
          className="neu-input px-2 py-1.5 rounded-lg bg-card border border-border">
          <option value="">Todos os distritos</option>
          {distritos.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={soPendentes} onChange={e => setSoPendentes(e.target.checked)} />
          só quem ainda não pagou
        </label>
      </div>

      {temFiltro && (
        <div className="pln-barra-linha pln-barra-fio text-xs">
          <FiFilter className="w-3.5 h-3.5 text-muted-foreground" />
          {fEtapa && <Chip onX={() => setFEtapa('')}>
            chegou até <strong>{ETAPAS.find(e => e.k === fEtapa)?.label}</strong>
          </Chip>}
          {fDesfecho && <Chip onX={() => setFDesfecho('')}>parou em <strong>{fDesfecho}</strong></Chip>}
          {fDistrito && <Chip onX={() => setFDistrito('')}>distrito <strong>{fDistrito}</strong></Chip>}
          {fLider && <Chip onX={() => setFLider('')}>líder <strong>{liderFiltrada}</strong></Chip>}
          {soPendentes && <Chip onX={() => setSoPendentes(false)}>ainda não pagou</Chip>}
          {q && <Chip onX={() => setQ('')}>busca “<strong>{q}</strong>”</Chip>}
          <button onClick={limparFiltros} className="px-2 py-1 rounded-lg text-muted-foreground hover:text-foreground underline underline-offset-2">
            limpar tudo
          </button>
        </div>
      )}
      </PlnBarra>

      {/* A contagem que ficava solta à direita dos filtros mora na nota do bloco. */}
      <PlnBloco
        titulo={aba === 'devedoras' ? 'Devedoras' : 'Por líder'}
        nota={aba === 'devedoras'
          ? `${num(filtradas.length)} de ${num(linhas.length)} devedoras`
          : `${num(porLider.length)} líderes no recorte · clique numa líder para ver as devedoras dela`}
        className="flex-1 min-h-0"
      >
      {aba === 'devedoras'
        ? <TabelaDevedoras linhas={filtradas} ord={ordD} onOrd={setOrdD} limite={limite}
            onMais={() => setLimite(l => l + 300)} onAbrir={setAberta} />
        : <TabelaLideres lideres={porLider} ord={ordL} onOrd={setOrdL}
            onFiltrar={(cod: string) => { setFLider(cod); setAba('devedoras') }} />}
      </PlnBloco>

      {aberta && campanhaId && (
        <DetalheDevedora
          campanhaId={campanhaId} linha={aberta}
          onClose={() => setAberta(null)}
          onMudou={() => carregar()}
          onOpenConversation={onOpenConversation}
        />
      )}
    </PanelShell>
  )
}

function Chip({ children, onX }: any) {
  return (
    <span className="neu-tag px-2 py-1 rounded-lg bg-primary/10 text-primary inline-flex items-center gap-1.5">
      {children}
      <button onClick={onX} className="opacity-70 hover:opacity-100" title="remover filtro"><FiX className="w-3 h-3" /></button>
    </span>
  )
}

// PLANOS (21/10/2026): título/sub da tela (eram o h1 do Cabecalho).
const TITULO_CAMPANHAS = 'Campanha de negociação de débito'
const SUB_CAMPANHAS = 'Rastreio da campanha de cobrança e apoio ao comissionamento das líderes'

// ── Ações do cabeçalho (PLANOS 21/10/2026: saíram do Cabecalho p/ `acoes`) ──
function AcoesCampanha({ busy, loading, isAdmin, onAtualizarPagamentos, onReprocessar, onRecarregar, onCsv, onXlsx }: any) {
  return (
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={onRecarregar} disabled={!!busy || loading} title="Recarregar o que vem ao vivo do crm"
            className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm flex items-center gap-1.5 disabled:opacity-60">
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'atualizando…' : 'atualizar'}
          </button>
          <button onClick={onAtualizarPagamentos} disabled={!!busy}
            title="Consulta o ERP e o Asaas para ver quem pagou"
            className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy === 'pagamentos' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDollarSign className="w-3.5 h-3.5" />}
            conferir pagamentos
          </button>
          <span className="w-px h-5 bg-border mx-0.5" />
          <button onClick={onCsv} disabled={!!busy} title="Baixar a campanha inteira em CSV"
            className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy === 'csv' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />} CSV
          </button>
          <button onClick={onXlsx} disabled={!!busy} title="Baixar o recorte atual em Excel (duas abas)"
            className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy === 'xlsx' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />} Excel
          </button>
          {isAdmin && (
            <>
              <span className="w-px h-5 bg-border mx-0.5" />
              <button onClick={onReprocessar} disabled={!!busy}
                title="Reapura grupo e líder no ERP. Não apaga anotações nem a dívida de referência."
                className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm text-muted-foreground disabled:opacity-60">
                {busy === 'snapshot' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : 'reapurar líderes'}
              </button>
            </>
          )}
        </div>
  )
}

// ── Cabeçalho: o funil clicável, o dinheiro e as datas de validade ────────
function Cabecalho({ bot, snapshotEm, totais, error, temFiltro,
                     fEtapa, onEtapa, funilAberto, onFunil }: any) {
  const modoBot = bot?.modo
  // A régua do % é a POPULAÇÃO do recorte, não "enviadas": assim uma falha de
  // envio aparece como etapa que não chegou a 100%, em vez de sumir na conta.
  const base = totais.total || 1

  return (
    <div className="space-y-2.5">
      {error && <Faixa tom="erro" icon={FiAlertTriangle}>{error}</Faixa>}
      {/* Sem isto a tela mente por omissão: se a régua não está em 'campanha',
          os números do bot simplesmente param de crescer e ninguém sabe. */}
      {modoBot && modoBot !== 'campanha' && (
        <Faixa tom="alerta" icon={FiSlash}>
          A régua automática está em <strong>“{modoBot}”</strong> — o bot não avança as etapas desta campanha.
          Oferta, negociação e acordo ficam parados nos números abaixo até a régua voltar para <strong>campanha</strong>
          {' '}(painel Conversas WhatsApp).
        </Faixa>
      )}
      {totais.falhas > 0 && (
        <Faixa tom="erro" icon={FiAlertTriangle}>
          {num(totais.falhas)} envio(s) falharam e nunca chegaram — filtre por <strong>parou em: Falhou</strong> para ver quem.
        </Faixa>
      )}
      {!totais.pagamentoEm && (
        <Faixa tom="alerta" icon={FiDollarSign}>
          Os pagamentos nunca foram conferidos nesta campanha: “recuperado” está zerado por falta de consulta, não por falta de pagamento.
          Clique em <strong>conferir pagamentos</strong>.
        </Faixa>
      )}
      {totais.promessasVencidas > 0 && (
        <Faixa tom="alerta" icon={FiClock}>
          {num(totais.promessasVencidas)} promessa(s) de pagamento venceram sem baixa no ERP.
        </Faixa>
      )}

      {funilAberto ? (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-3 items-start">
          {/* Funil ─ a queda entre etapas é a informação, não o valor absoluto. */}
          <div className="neu-card rounded-xl border border-border bg-card px-3.5 py-3">
            <div className="flex items-baseline justify-between mb-1">
              <BotaoFunil aberto onClick={onFunil} temFiltro={temFiltro} />
              <span className="text-xs text-muted-foreground">clique numa etapa para filtrar quem chegou até ela</span>
            </div>
            {totais.funil.map((e: any, i: number) => {
              const ant = i > 0 ? totais.funil[i - 1].v : null
              const perda = ant != null && ant > e.v ? ant - e.v : 0
              const pct = base ? (e.v / base) * 100 : 0
              const on = fEtapa === e.k
              return (
                <button key={e.k} onClick={() => onEtapa(e.k)} disabled={!e.v && !on}
                  className={`w-full flex items-center gap-2 px-1.5 py-[3px] rounded-md text-left transition-colors disabled:cursor-default
                    ${on ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-secondary/60'}`}>
                  <span className={`w-[112px] shrink-0 text-xs truncate ${on ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    {e.label}
                  </span>
                  <span className="flex-1 h-2 rounded-full bg-secondary/70 overflow-hidden">
                    <span className={`block h-full rounded-full ${e.barra}`} style={{ width: `${Math.max(pct, e.v ? 1 : 0)}%` }} />
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs tabular-nums font-medium">{num(e.v)}</span>
                  <span className="w-11 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {Math.round(pct)}%
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground/80">
                    {perda ? `−${num(perda)}` : ''}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Dinheiro e procedência ─ zero NÃO é verde. */}
          <div className="neu-card rounded-xl border border-border bg-card px-3.5 py-3 flex flex-col gap-2.5 text-[13px]">
            <Valor rot="Dívida de referência" val={brl(totais.divida)} forte />
            <Valor rot="Recuperado no ERP" val={brl(totais.recuperado)} bom={totais.recuperado > 0} />
            <Valor rot="Acordo pago (Asaas)" val={brl(totais.acordoPago)} bom={totais.acordoPago > 0} />
            {/* Não se somam: o boleto do Asaas é externo ao ERP e não baixa lá. */}
            <div className="border-t border-border/70 mt-0.5 pt-1.5 space-y-1 text-muted-foreground">
              <div>
                <strong className="text-foreground">{num(totais.lideres)}</strong> líderes distintas ·{' '}
                <strong className="text-foreground">{num(totais.lideresAtivas)}</strong> ativas ·{' '}
                <strong className="text-foreground">{num(totais.lideresAvisadas)}</strong> já avisadas
              </div>
              <div>
                {num(totais.propriaLider)} devedoras eram a própria líder · {num(totais.semLider)} sem líder identificada
              </div>
              {totais.promessasAbertas > 0 && (
                <div className={totais.promessasVencidas > 0 ? 'text-amber-600 dark:text-amber-500' : ''}>
                  {num(totais.promessasAbertas)} promessa(s) em aberto
                  {totais.promessasVencidas > 0 ? ` · ${num(totais.promessasVencidas)} vencida(s)` : ''}
                </div>
              )}
              <div className="flex items-start gap-1 pt-0.5">
                <FiClock className="w-3 h-3 mt-[2px] shrink-0" />
                <span>
                  líderes apuradas em {dataHora(snapshotEm)}<br />
                  {totais.pagamentoEm ? `pagamentos conferidos em ${dataHora(totais.pagamentoEm)}` : 'pagamentos ainda não conferidos'}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Recolhido é UMA faixa: manter os dois cards lado a lado devolveria a
        // altura do card do dinheiro e o gesto não economizaria nada.
        <div className="neu-card rounded-xl border border-border bg-card px-3.5 py-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]">
          <BotaoFunil aberto={false} onClick={onFunil} temFiltro={temFiltro} />
          {totais.funil.map((e: any) => (
            <button key={e.k} onClick={() => onEtapa(e.k)} disabled={!e.v && fEtapa !== e.k}
              className={`px-1.5 py-0.5 rounded-md disabled:cursor-default ${fEtapa === e.k
                ? 'bg-primary/10 text-primary ring-1 ring-primary/40' : 'hover:bg-secondary/60'}`}>
              <span className="text-muted-foreground">{e.label} </span>
              <strong className="tabular-nums">{num(e.v)}</strong>
            </button>
          ))}
          <span className="w-px h-4 bg-border" />
          <span className="text-muted-foreground">dívida <strong className="text-foreground tabular-nums">{brl(totais.divida)}</strong></span>
          <span className="text-muted-foreground">
            recuperado <strong className={`tabular-nums ${totais.recuperado > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>{brl(totais.recuperado)}</strong>
          </span>
          <span className="text-muted-foreground">
            acordo pago <strong className={`tabular-nums ${totais.acordoPago > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>{brl(totais.acordoPago)}</strong>
          </span>
          <span className="text-muted-foreground">
            <strong className="text-foreground">{num(totais.lideres)}</strong> líderes · {num(totais.lideresAtivas)} ativas · {num(totais.lideresAvisadas)} avisadas
          </span>
          {totais.promessasAbertas > 0 && (
            <span className={totais.promessasVencidas > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'}>
              {num(totais.promessasAbertas)} promessa(s){totais.promessasVencidas > 0 ? ` · ${num(totais.promessasVencidas)} vencida(s)` : ''}
            </span>
          )}
          <span className="flex-1" />
          <span className="text-muted-foreground inline-flex items-center gap-1"
            title={`líderes apuradas em ${dataHora(snapshotEm)}`}>
            <FiClock className="w-3 h-3" />
            {totais.pagamentoEm ? `pagamentos em ${dataHora(totais.pagamentoEm)}` : 'pagamentos não conferidos'}
          </span>
        </div>
      )}
    </div>
  )
}

function BotaoFunil({ aberto, onClick, temFiltro }: any) {
  return (
    <button onClick={onClick} title={aberto ? 'recolher o funil' : 'expandir o funil'}
      className="text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0">
      {aberto ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
      Funil {temFiltro ? 'do recorte' : 'da campanha'}
    </button>
  )
}

function Faixa({ tom, icon: Icon, children }: any) {
  const cls = tom === 'erro'
    ? 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'
    : 'bg-amber-500/10 text-amber-800 dark:text-amber-400 border-amber-500/30'
  return (
    <div className={`px-3 py-2 rounded-lg border text-sm flex items-start gap-2 ${cls}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <span className="leading-snug">{children}</span>
    </div>
  )
}

function Valor({ rot, val, forte, bom }: any) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{rot}</span>
      <span className={`tabular-nums ${forte ? 'text-sm font-semibold' : ''} ${bom ? 'text-emerald-600 dark:text-emerald-400 font-medium' : ''}`}>
        {val}
      </span>
    </div>
  )
}

// Cabeçalho de coluna ordenável. A seta some quando a coluna não está ativa,
// mas o espaço fica reservado — senão a largura pula a cada clique.
function Th({ col, ord, onOrd, dir0 = -1, className = '', title, children }: any) {
  const ativo = ord && ord.col === col
  if (!col) return <th className={`${stickyTh} px-3 py-2 ${className}`} title={title}>{children}</th>
  return (
    <th className={`${stickyTh} px-3 py-2 ${className}`} title={title}>
      <button onClick={() => onOrd(ativo ? { col, dir: -ord.dir } : { col, dir: dir0 })}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${ativo ? 'text-foreground font-medium' : ''}`}>
        {children}
        {ativo
          ? (ord.dir === 1 ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />)
          : <FiChevronDown className="w-3 h-3 opacity-0" />}
      </button>
    </th>
  )
}

// ── Tabela de devedoras ───────────────────────────────────────────────────
const COLS_DEV: Record<string, (l: Linha) => any> = {
  consultora: l => l.nome,
  divida: l => Number(l.saldo_snapshot),
  recuperado: l => Number(l.valor_recuperado || 0),
  atraso: l => l.dias_atraso,
  grupo: l => l.grupo_nome,
  lider: l => (l.eh_propria_lider ? 'ELA MESMA' : l.lider_nome),
  jornada: l => MARCOS.filter(m => m.tem(l)).length,
  desfecho: l => DESFECHO_ORDEM.indexOf(l.desfecho),
}

function TabelaDevedoras({ linhas, ord, onOrd, limite, onMais, onAbrir }: any) {
  const ordenadas = useMemo(
    () => ordenar<Linha>(linhas, COLS_DEV[ord.col] || COLS_DEV.divida, ord.dir,
                         l => Number(l.saldo_snapshot)), [linhas, ord])
  const visiveis = ordenadas.slice(0, limite)
  return (
    <div className="flex-1 min-h-0 overflow-auto scrollbar-thin neu-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <Th col="consultora" ord={ord} onOrd={onOrd} dir0={1}>Consultora</Th>
            <Th col="divida" ord={ord} onOrd={onOrd} className="text-right">Dívida</Th>
            <Th col="atraso" ord={ord} onOrd={onOrd} className="text-right" title="Dias desde o vencimento mais antigo">Atraso</Th>
            <Th col="grupo" ord={ord} onOrd={onOrd} dir0={1}>Grupo / distrito</Th>
            <Th col="lider" ord={ord} onOrd={onOrd} dir0={1}>Última líder</Th>
            <Th col="jornada" ord={ord} onOrd={onOrd} title="Entregue · Leu · Clicou · Respondeu · Bot">Jornada</Th>
            <Th col="desfecho" ord={ord} onOrd={onOrd}>Desfecho e marcações</Th>
          </tr>
        </thead>
        <tbody>
          {visiveis.map((l: Linha) => (
            <tr key={l.codigo} onClick={() => onAbrir(l)}
              className="border-t border-border hover:bg-secondary/50 cursor-pointer align-top">
              <td className="px-3 py-2">
                <div className="font-medium truncate max-w-[280px]">{l.nome}</div>
                <div className="text-xs text-muted-foreground">{l.codigo} · {fone(l.telefone)}</div>
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                <div>{brl(l.saldo_snapshot)}</div>
                {Number(l.valor_recuperado) > 0 && (
                  <div className="text-xs text-emerald-600 dark:text-emerald-400">−{brl(l.valor_recuperado)} pago</div>
                )}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap text-muted-foreground tabular-nums"
                title={l.dias_atraso != null ? `${num(l.dias_atraso)} dias · ${l.titulos} título(s)` : ''}>
                {atrasoTxt(l.dias_atraso)}
              </td>
              <td className="px-3 py-2">
                <div className="truncate max-w-[190px]">{l.grupo_nome || '—'}
                  {l.grupo_extinto && <span className="text-xs text-muted-foreground"> (extinto)</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate max-w-[190px]">{l.distrito_nome}</div>
              </td>
              <td className="px-3 py-2">
                {l.eh_propria_lider ? (
                  <span className="text-xs neu-tag px-2 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-500">
                    ela mesma era a líder
                  </span>
                ) : (
                  <>
                    <div className="truncate max-w-[210px]">{l.lider_nome || '—'}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      {l.lider_ativa ? 'ativa' : 'inativa'}
                      {l.lider_match === 'aprox' && <span title="A líder foi inferida pela linha mais próxima anterior, não pela semana exata">· aprox.</span>}
                    </div>
                  </>
                )}
              </td>
              <td className="px-3 py-2"><Jornada l={l} /></td>
              <td className="px-3 py-2">
                <span className={`neu-tag px-2 py-0.5 rounded text-xs whitespace-nowrap ${DESFECHO_CLS[l.desfecho] || 'bg-muted'}`}>
                  {l.desfecho}
                </span>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5 empty:mt-0">
                  {Number(l.acordo_parcelas_total) > 0 && (
                    <div className="text-teal-700 dark:text-teal-400">
                      {l.acordo_origem === 'manual' ? 'acordo no atendimento' : 'acordo pelo bot'}
                      {' · '}{l.acordo_parcelas_pagas || 0}/{l.acordo_parcelas_total}
                    </div>
                  )}
                  {l.lider_acionada_em && <div className="text-emerald-600 dark:text-emerald-400">líder avisada</div>}
                  {l.promessa_data && <div>promete {dia(l.promessa_data)}{l.promessa_valor ? ` · ${brl(l.promessa_valor)}` : ''}</div>}
                  {l.eventos > 0 && <div className="flex items-center gap-1"><FiFileText className="w-3 h-3" />{l.eventos} registro(s)</div>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {ordenadas.length > visiveis.length && (
        <div className="p-3 text-center">
          <button onClick={onMais} className="neu-btn px-3 py-1.5 rounded-lg border border-border text-sm">
            mostrar mais ({num(ordenadas.length - visiveis.length)} restantes)
          </button>
        </div>
      )}
      {!linhas.length && <div className="p-8 text-center text-muted-foreground text-sm">nenhuma devedora com esses filtros</div>}
    </div>
  )
}

// As bolinhas do funil na linha. Apagado = não aconteceu — sem traço, sem
// coluna de "—": ausência aqui é informação, não buraco.
function Jornada({ l }: { l: Linha }) {
  if (l.envio_status === 'failed') {
    return <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={l.erro || 'falha no envio'}>
      <FiAlertTriangle className="w-3.5 h-3.5" /> não chegou
    </span>
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {MARCOS.map(m => {
        const on = m.tem(l)
        const n = on && m.n ? m.n(l) : 0
        const quando = on && m.quando ? m.quando(l) : null
        const Icon = m.icon
        const rot = m.k === 'bot' && l.bot_estado ? (ETAPA_BOT[l.bot_estado] || l.bot_estado)
          : m.k === 'acordo'
            ? `Acordo ${l.acordo_origem === 'manual' ? 'fechado no atendimento' : 'do bot'} · ${l.acordo_parcelas_pagas || 0} de ${l.acordo_parcelas_total} parcela(s) paga(s)`
            : m.label
        return (
          <span key={m.k} className="inline-flex items-center"
            title={on ? `${rot}${quando ? ` · ${dataHora(quando)}` : ''}${n > 1 ? ` · ${n}×` : ''}` : `${m.label}: não`}>
            <Icon className={`w-3.5 h-3.5 ${on ? m.cor : 'text-muted-foreground/20'}`} />
            {n > 1 && <span className="text-xs text-muted-foreground ml-px">{n}</span>}
          </span>
        )
      })}
    </span>
  )
}

// ── Tabela por líder — a visão de comissionamento ─────────────────────────
const COLS_LID: Record<string, (a: any) => any> = {
  lider: a => a.nome,
  devedoras: a => a.devedoras,
  divida: a => a.divida,
  recuperado: a => a.recuperado,
  pagaram: a => a.pagaram,
  responderam: a => a.responderam,
  acionadas: a => a.acionadas,
}

function TabelaLideres({ lideres, ord, onOrd, onFiltrar }: any) {
  // Desempate pela dívida: com recuperado zerado em quase todo mundo, sem ele a
  // ordem entre líderes empatadas ficaria arbitrária.
  const ordenadas = useMemo(
    () => ordenar<any>(lideres, COLS_LID[ord.col] || COLS_LID.recuperado, ord.dir, a => a.divida),
    [lideres, ord])
  const maiorDivida = useMemo(() => lideres.reduce((m: number, a: any) => Math.max(m, a.divida), 0), [lideres])
  return (
    <div className="flex-1 min-h-0 overflow-auto scrollbar-thin neu-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <Th col="lider" ord={ord} onOrd={onOrd} dir0={1}>Última líder</Th>
            <Th col="devedoras" ord={ord} onOrd={onOrd} className="text-right">Devedoras</Th>
            <Th col="divida" ord={ord} onOrd={onOrd} className="text-right">Dívida na carteira</Th>
            <Th col="recuperado" ord={ord} onOrd={onOrd} className="text-right">Recuperado</Th>
            <Th col="pagaram" ord={ord} onOrd={onOrd} className="text-right">Pagaram</Th>
            <Th col="responderam" ord={ord} onOrd={onOrd} className="text-right">Responderam</Th>
            <Th col="acionadas" ord={ord} onOrd={onOrd} className="text-right" title="Devedoras cuja líder já foi avisada">Avisadas</Th>
            <Th ord={ord} onOrd={onOrd}>Distrito</Th>
          </tr>
        </thead>
        <tbody>
          {ordenadas.map((a: any, i: number) => {
            const wa = a.propria ? null : waHref(a.fone)
            return (
              <tr key={a.cod || `x${i}`} onClick={() => a.cod && onFiltrar(a.cod)}
                className={`border-t border-border ${a.cod ? 'hover:bg-secondary/50 cursor-pointer' : ''}`}>
                <td className="px-3 py-2">
                  <div className={`font-medium ${a.propria ? 'text-amber-700 dark:text-amber-500' : ''}`}>{a.nome}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    {a.cod ? (
                      <>
                        <span>{a.cod} · {fone(a.fone)} · {a.ativa ? 'ativa' : 'inativa'}</span>
                        {wa && (
                          <a href={wa} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                            title="Falar com a líder no WhatsApp"
                            className="text-primary hover:underline inline-flex items-center gap-0.5">
                            <FiExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </>
                    ) : 'vai para o gerente de distrito'}
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{a.devedoras}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  <div>{brl(a.divida)}</div>
                  {/* Cresce da DIREITA: numa coluna alinhada à direita, uma barra
                      que começa na esquerda lê como sublinhado solto. */}
                  <span className="flex justify-end h-1 mt-1 rounded-full bg-border overflow-hidden">
                    <span className="h-full rounded-full bg-slate-400/80"
                      style={{ width: `${maiorDivida ? (a.divida / maiorDivida) * 100 : 0}%` }} />
                  </span>
                </td>
                <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${a.recuperado > 0 ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-muted-foreground'}`}>
                  {brl(a.recuperado)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{a.pagaram || <span className="text-muted-foreground/40">0</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.responderam || <span className="text-muted-foreground/40">0</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.acionadas || <span className="text-muted-foreground/40">0</span>}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[220px]" title={[...a.distritos].join(', ')}>
                  {[...a.distritos].join(', ')}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {!lideres.length && <div className="p-8 text-center text-muted-foreground text-sm">nenhuma líder com esses filtros</div>}
    </div>
  )
}

// ── Drawer: o dossiê da devedora e o que o atendimento combinou ───────────
function DetalheDevedora({ campanhaId, linha, onClose, onMudou, onOpenConversation }: any) {
  const { authenticatedFetch } = useAuth()
  const [dados, setDados] = useState<{ devedora: any; eventos: Evento[] } | null>(null)
  const [nota, setNota] = useState('')
  const [pData, setPData] = useState('')
  const [pValor, setPValor] = useState('')
  const [salvando, setSalvando] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const base = `/api/campanhas/${campanhaId}/devedora/${linha.codigo}`
  const carregar = useCallback(async () => {
    try {
      const r = await authenticatedFetch(base)
      const d = await r.json()
      if (!r.ok) throw new Error(d.error)
      setDados(d)
      setPData(d.devedora.promessa_data ? String(d.devedora.promessa_data).slice(0, 10) : '')
      setPValor(d.devedora.promessa_valor ? String(d.devedora.promessa_valor) : '')
    } catch (e: any) { setErro(e.message) }
  }, [authenticatedFetch, base])
  useEffect(() => { carregar() }, [carregar])

  const enviar = async (acao: string, url: string, init: RequestInit) => {
    setSalvando(acao); setErro(null)
    try {
      const r = await authenticatedFetch(url, init)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'falhou')
      if (acao === 'nota') setNota('')
      await carregar(); onMudou()
    } catch (e: any) { setErro(e.message) } finally { setSalvando(null) }
  }

  // A rota /devedora devolve só a tabela fria — engajamento não mora lá. A
  // linha da tabela é quem tem entrega/leitura/clique, então ela vem primeiro
  // e o fetch só sobrescreve o que de fato existe no banco (estado manual).
  const d = { ...linha, ...(dados?.devedora || {}) }
  const propria = !!d.eh_propria_lider
  const waDevedora = waHref(d.telefone)
  const waLider = propria ? null : waHref(d.lider_fone)
  const recuperado = Number(d.valor_recuperado || 0)
  const disparoDifere = d.saldo_disparo != null && Math.abs(Number(d.saldo_disparo) - Number(d.saldo_snapshot)) >= 0.01

  return (
    // PLANOS (21/10/2026): cada seção da gaveta é uma caixa com respiro p-4 e
    // título 15px (antes rótulo 12px caixa-alta); botões h-9, rótulos 12.5px.
    <SideModal
      title={d.nome || linha.codigo}
      subtitle={`${linha.codigo} · ${fone(d.telefone)}`}
      icon={FiUser} onClose={onClose} maxWidth="max-w-2xl"
      footer={<>
        {waDevedora && (
          <a href={waDevedora} target="_blank" rel="noreferrer"
            className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center gap-1.5 border border-border text-sm">
            <FiExternalLink className="w-3.5 h-3.5" /> WhatsApp da devedora
          </a>
        )}
        {d.bot_conversation_id && onOpenConversation && (
          <button onClick={() => onOpenConversation(d.bot_conversation_id)}
            className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground text-sm">
            <FiMessageCircle className="w-3.5 h-3.5" /> abrir a conversa
          </button>
        )}
        <button onClick={onClose} className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center border border-border text-sm">fechar</button>
      </>}
    >
      {erro && <div className="px-3 py-2 rounded-lg bg-red-500/10 text-red-600 text-sm">{erro}</div>}

      <div className="flex items-center gap-2">
        <span className={`neu-tag px-2 py-0.5 rounded text-xs ${DESFECHO_CLS[linha.desfecho] || 'bg-muted'}`}>{linha.desfecho}</span>
        {/* `!!`: a rota /devedora devolve a coluna crua do MySQL (0/1), e `0 &&`
            faz o React imprimir um "0" solto ao lado do desfecho. */}
        {!!d.grupo_extinto && <span className="text-xs text-muted-foreground">grupo extinto</span>}
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">últ. atividade {semana(d.ult_semana) || '—'}</span>
      </div>

      <div className="rounded-xl border border-border p-4">
        <div className="text-[15px] font-semibold text-foreground mb-3">O dinheiro</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Campo rot="Dívida de referência" val={brl(d.saldo_snapshot)} hint="a base da comissão — congelada" />
          <Campo rot="Dívida hoje no ERP" val={d.saldo_atual != null ? brl(d.saldo_atual) : 'não conferida'} />
          <Campo rot="Recuperado" val={recuperado > 0 ? brl(recuperado) : '—'} destaque={recuperado > 0} />
          <Campo rot="Últ. pagamento no ERP" val={dia(d.ult_pagamento_erp) || '—'} />
        </div>
        {disparoDifere && (
          <p className="text-[12.5px] text-muted-foreground mt-3">
            A mensagem cobrou {brl(d.saldo_disparo)} — a dívida de referência mudou depois do disparo.
          </p>
        )}
        <div className="text-[12.5px] text-muted-foreground mt-3">
          {d.titulos} título(s) · {num(d.dias_atraso)} dias de atraso ·{' '}
          {d.grupo_nome || 'sem grupo'} · {d.distrito_nome || 'sem distrito'}
        </div>
      </div>

      {/* A jornada: era o dado mais rico do painel e não aparecia em lugar
          nenhum além de uma palavra na coluna Desfecho. */}
      <div className="rounded-xl border border-border p-4">
        <div className="text-[15px] font-semibold text-foreground mb-3">A jornada da mensagem</div>
        <Timeline l={d} />
      </div>

      <div className="rounded-xl border border-border p-4 space-y-1.5">
        <div className="text-[15px] font-semibold text-foreground">A líder para comissionar</div>
        {propria ? (
          <p className="text-sm text-amber-700 dark:text-amber-500">
            Ela mesma era a líder do grupo. Não dá para comissionar alguém para cobrar de si —
            este caso vai para o gerente de distrito.
          </p>
        ) : (
          <>
            <div className="font-medium">{d.lider_nome || 'não identificada'}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span>{d.lider_cod} · {fone(d.lider_fone)} · {d.lider_ativa ? 'ativa em 2026' : 'inativa'}</span>
              {waLider && (
                <a href={waLider} target="_blank" rel="noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                  <FiExternalLink className="w-3 h-3" /> chamar no WhatsApp
                </a>
              )}
            </div>
            {d.lider_match === 'aprox' && (
              <p className="text-xs text-muted-foreground">
                Vínculo aproximado: o grupo estava parado, então vale a líder registrada na
                semana mais próxima anterior ({semana(d.lider_ult_semana)}).
              </p>
            )}
            <button
              onClick={() => enviar('lider', base, {
                method: 'PATCH', body: JSON.stringify({ lider_acionada: !d.lider_acionada_em }),
              })}
              disabled={!!salvando}
              className={`neu-btn mt-2 h-9 px-3.5 rounded-lg inline-flex items-center justify-center text-sm ${d.lider_acionada_em
                ? 'border border-border text-muted-foreground'
                : 'bg-primary text-primary-foreground'}`}>
              {salvando === 'lider' ? '…' : d.lider_acionada_em
                ? `líder avisada em ${dataHora(d.lider_acionada_em)} — desfazer`
                : 'marcar líder como avisada'}
            </button>
            {d.lider_acionada_nome && (
              <div className="text-xs text-muted-foreground">por {d.lider_acionada_nome}</div>
            )}
          </>
        )}
      </div>

      <div className="rounded-xl border border-border p-4 space-y-2.5">
        <div className="text-[15px] font-semibold text-foreground">Promessa de pagamento</div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[12.5px] text-muted-foreground">
            data
            <input type="date" value={pData} onChange={e => setPData(e.target.value)}
              className="neu-input block mt-1 h-9 px-2.5 rounded-lg bg-card border border-border text-sm text-foreground" />
          </label>
          <label className="text-[12.5px] text-muted-foreground">
            valor (opcional)
            <input value={pValor} onChange={e => setPValor(e.target.value)} placeholder="350,00"
              className="neu-input block mt-1 h-9 px-2.5 rounded-lg bg-card border border-border text-sm w-28" />
          </label>
          <button
            onClick={() => enviar('promessa', base, {
              method: 'PATCH',
              body: JSON.stringify({ promessa_data: pData || null, promessa_valor: pData ? (pValor || null) : null }),
            })}
            disabled={!!salvando}
            className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center bg-primary text-primary-foreground text-sm disabled:opacity-60">
            {salvando === 'promessa' ? '…' : 'gravar'}
          </button>
          {d.promessa_data && (
            <button onClick={() => { setPData(''); setPValor('')
              enviar('promessa', base, { method: 'PATCH', body: JSON.stringify({ promessa_data: null, promessa_valor: null }) }) }}
              disabled={!!salvando}
              className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center border border-border text-sm text-muted-foreground">
              limpar
            </button>
          )}
        </div>
        {/* Ao telefone se combina "sexta", não "2026-08-29". */}
        <div className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
          <span className="text-muted-foreground">rápido:</span>
          {[['hoje', 0], ['amanhã', 1], ['em 3 dias', 3], ['em 7 dias', 7], ['em 15 dias', 15]].map(([rot, n]: any) => (
            <button key={rot} onClick={() => setPData(emDias(n))}
              className="neu-tag px-2.5 py-1 rounded-lg bg-secondary/70 hover:bg-secondary text-muted-foreground hover:text-foreground">
              {rot}
            </button>
          ))}
          {pData && <span className="text-muted-foreground">→ {dia(pData)}</span>}
        </div>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-2.5">
        <div className="text-[15px] font-semibold text-foreground">Anotar</div>
        <textarea value={nota} onChange={e => setNota(e.target.value)} rows={3}
          placeholder="o que foi combinado, com quem, o que ficou pendente…"
          className="neu-input w-full px-3 py-2 rounded-lg bg-card border border-border text-sm" />
        <button onClick={() => enviar('nota', `${base}/nota`, { method: 'POST', body: JSON.stringify({ texto: nota }) })}
          disabled={!nota.trim() || !!salvando}
          className="neu-btn h-9 px-3.5 rounded-lg inline-flex items-center justify-center bg-primary text-primary-foreground text-sm disabled:opacity-50">
          {salvando === 'nota' ? '…' : 'gravar anotação'}
        </button>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-2.5">
        <div className="text-[15px] font-semibold text-foreground">Histórico</div>
        {!dados?.eventos.length && <p className="text-sm text-muted-foreground">nada registrado ainda.</p>}
        {[...(dados?.eventos || [])].reverse().map(e => (
          <div key={e.id} className="text-sm border-l-2 border-border pl-3 py-1">
            <div className="text-xs text-muted-foreground">
              {dataHora(e.criado_em)} · {e.autor_nome || 'sistema'}
            </div>
            <div>
              {e.tipo === 'nota' && e.texto}
              {e.tipo === 'promessa' && (e.promessa_data
                ? `prometeu pagar em ${dia(e.promessa_data)}${e.promessa_valor ? ` · ${brl(e.promessa_valor)}` : ''}`
                : 'promessa removida')}
              {e.tipo === 'lider_acionada' && e.texto}
              {e.tipo === 'sistema' && e.texto}
            </div>
          </div>
        ))}
      </div>
    </SideModal>
  )
}

// Linha do tempo do disparo. Marco que não aconteceu fica apagado em vez de
// sumir: saber que a mensagem NÃO foi lida é metade do diagnóstico.
function Timeline({ l }: { l: any }) {
  const marcos: { icon: any; rot: string; quando: string | null; ok: boolean; extra?: string; cor?: string }[] = [
    { icon: FiSend, rot: 'Enviada', quando: l.enviado_em, ok: !!l.enviado_em || l.envio_status === 'sent', cor: 'text-slate-600 dark:text-slate-300' },
    { icon: FiCheckCircle, rot: 'Entregue no aparelho', quando: l.entregue_em, ok: !!l.entregue_em, cor: 'text-slate-600 dark:text-slate-300' },
    { icon: FiEye, rot: 'Lida', quando: l.lida_em, ok: !!l.lida_em, cor: 'text-sky-600 dark:text-sky-400' },
    { icon: FiMousePointer, rot: 'Clicou no link', quando: l.clicou_em, ok: !!l.clicou_em, cor: 'text-blue-600 dark:text-blue-400',
      extra: l.cliques > 1 ? `${l.cliques} cliques` : undefined },
    { icon: FiMessageCircle, rot: 'Respondeu', quando: null, ok: !!l.respondeu, cor: 'text-violet-600 dark:text-violet-400',
      extra: l.msgs_recebidas ? `${l.msgs_recebidas} mensagem(ns) recebida(s)` : undefined },
    { icon: FiZap, rot: l.bot_estado ? `Bot: ${ETAPA_BOT[l.bot_estado] || l.bot_estado}` : 'Bot não avançou', quando: null, ok: !!l.bot_estado,
      cor: 'text-indigo-600 dark:text-indigo-400',
      extra: l.valor_ofertado ? `ofertou ${brl(l.valor_ofertado)}${l.parcelas ? ` em ${l.parcelas}x` : ''}` : undefined },
    { icon: FiFileText,
      rot: Number(l.acordo_parcelas_total) > 0
        ? `Acordo ${l.acordo_origem === 'manual' ? 'fechado no atendimento' : 'fechado pelo bot'}`
        : 'Acordo (boleto Asaas)',
      quando: l.acordo_ult_pagamento, ok: Number(l.acordo_parcelas_total) > 0,
      cor: 'text-teal-600 dark:text-teal-400',
      extra: Number(l.acordo_parcelas_total) > 0
        ? `${(l.acordo_qtd || 1) > 1 ? `${l.acordo_qtd} acordos · ` : ''}${l.acordo_parcelas_pagas || 0} de ${l.acordo_parcelas_total} parcela(s) paga(s)${Number(l.acordo_valor_pago) > 0 ? ` · ${brl(l.acordo_valor_pago)} pago` : ''}`
        : undefined },
    { icon: FiDollarSign, rot: 'Baixou no ERP', quando: l.ult_pagamento_erp, ok: Number(l.valor_recuperado) > 0,
      cor: 'text-emerald-600 dark:text-emerald-400',
      extra: Number(l.valor_recuperado) > 0 ? brl(l.valor_recuperado) : undefined },
  ]
  if (l.envio_status === 'failed') {
    return (
      <div className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
        <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>A mensagem não foi entregue{l.erro ? `: ${l.erro}` : '.'} Nada do funil aconteceu para esta pessoa.</span>
      </div>
    )
  }
  return (
    <ol className="space-y-1.5">
      {marcos.map((m, i) => {
        const Icon = m.icon
        return (
          <li key={i} className="flex items-center gap-2 text-sm">
            <Icon className={`w-4 h-4 shrink-0 ${m.ok ? m.cor : 'text-muted-foreground/25'}`} />
            <span className={m.ok ? '' : 'text-muted-foreground/60'}>{m.rot}</span>
            {m.extra && <span className="text-xs text-muted-foreground">· {m.extra}</span>}
            <span className="flex-1" />
            <span className="text-xs text-muted-foreground tabular-nums">
              {m.ok ? (m.quando ? dataHora(m.quando) : 'sim') : 'não'}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function Campo({ rot, val, destaque, hint }: { rot: string; val: string; destaque?: boolean; hint?: string }) {
  return (
    <div title={hint}>
      <div className="text-[12.5px] text-muted-foreground">{rot}</div>
      <div className={`text-base font-semibold mt-0.5 tabular-nums ${destaque ? 'text-emerald-600 dark:text-emerald-400 font-medium' : ''}`}>{val}</div>
    </div>
  )
}
